import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2, RotateCcw, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  listRecoverable,
  recoverRecording,
  clearRecording,
  MAX_RETAINED_RECORDINGS,
} from "../lib/recordingBackup";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";

const STROKE = 1.5;

// Sticky, app-wide recovery banner (mounted in AppShell so it persists across
// navigation). Surfaces interview recordings whose audio is captured on THIS
// device but never made it to the server (page-death / session-expiry mid-save).
//
// Product invariants enforced here:
//  - ALWAYS prompts a human; never auto-finalizes.
//  - NEVER silently auto-discards; Discard requires a destructive confirm.
//  - Recovery re-uploads to the SAME recording_id → SAME interview (consent
//    intact); it never creates a new, consent-less interview.
export default function RecoveryBanner() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [progress, setProgress] = useState(null); // { id, uploaded, total }
  const [discardTarget, setDiscardTarget] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setItems(await listRecoverable());
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("hireproof:recovery-changed", onChange);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("hireproof:recovery-changed", onChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const handleRecover = async (meta) => {
    setBusyId(meta.recordingId);
    setProgress({ id: meta.recordingId, uploaded: 0, total: 0 });
    try {
      const res = await recoverRecording(meta, {
        onProgress: ({ uploaded, total }) =>
          setProgress({ id: meta.recordingId, uploaded, total }),
      });
      if (res.ok) {
        toast.success(
          `Recovered ${meta.candidateName}${meta.roundNumber ? ` · Round ${meta.roundNumber}` : ""} — saved to the server.`
        );
        window.dispatchEvent(new CustomEvent("hireproof:recovery-changed"));
      } else if (res.reason === "no_chunks") {
        toast.error("No local audio was found for this recording.");
      } else {
        toast.error(
          "Some audio chunks failed to upload. Your audio is still safe on this device — check your connection and try again."
        );
      }
    } catch {
      toast.error(
        "Recovery failed — your audio is still safe on this device. Please try again."
      );
    } finally {
      setBusyId(null);
      setProgress(null);
      refresh();
    }
  };

  const confirmDiscard = async () => {
    const meta = discardTarget;
    setDiscardTarget(null);
    if (!meta) return;
    await clearRecording(meta.recordingId);
    toast("Recording discarded from this device.");
    refresh();
  };

  if (items.length === 0) return null;

  const atCapacity = items.length >= MAX_RETAINED_RECORDINGS;

  return (
    <div
      className="border-b border-amber-300 bg-amber-50"
      data-testid="recovery-banner"
      role="region"
      aria-label="Unsaved recordings"
    >
      <div className="mx-auto w-full max-w-6xl px-6 py-3 lg:px-10">
        <div className="flex items-start gap-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={STROKE} />
          <div className="flex-1 space-y-2.5">
            <div className="text-[13px] font-medium text-amber-900" data-testid="recovery-banner-headline">
              {items.length === 1
                ? "1 interview recording is saved on this device but not yet on the server."
                : `${items.length} interview recordings are saved on this device but not yet on the server.`}{" "}
              <span className="font-normal text-amber-800">
                Recover to finish saving each one — your audio is not lost.
              </span>
            </div>

            {atCapacity && (
              <div
                className="rounded-md border border-amber-300 bg-amber-100 px-3 py-1.5 text-[12px] text-amber-900"
                data-testid="recovery-capacity-warning"
              >
                Local backup is at capacity ({items.length}/{MAX_RETAINED_RECORDINGS}). Recover or discard
                some of these before starting a new interview — new recordings may not back up on this device.
              </div>
            )}

            <ul className="space-y-2">
              {items.map((meta) => {
                const busy = busyId === meta.recordingId;
                const p = progress && progress.id === meta.recordingId ? progress : null;
                return (
                  <li
                    key={meta.recordingId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white px-3 py-2"
                    data-testid={`recovery-item-${meta.recordingId}`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-ink-900">
                        {meta.candidateName || "Candidate"}
                        {meta.roundNumber ? ` · Round ${meta.roundNumber}` : ""}
                      </div>
                      <div className="text-[11px] text-ink-500">
                        {meta.durationSeconds
                          ? `~${Math.round(meta.durationSeconds / 60)} min captured`
                          : "Captured on this device"}
                        {meta.startedAt ? ` · ${new Date(meta.startedAt).toLocaleString()}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p && (
                        <span className="text-[11px] text-ink-500" data-testid={`recovery-progress-${meta.recordingId}`}>
                          {p.total ? `Uploading ${p.uploaded}/${p.total}…` : "Preparing…"}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRecover(meta)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-800 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-emerald-900 disabled:opacity-60"
                        data-testid={`recovery-recover-${meta.recordingId}`}
                      >
                        {busy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={STROKE} />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" strokeWidth={STROKE} />
                        )}
                        {busy ? "Recovering…" : "Recover"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDiscardTarget(meta)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-600 transition hover:bg-ink-50 disabled:opacity-60"
                        data-testid={`recovery-discard-${meta.recordingId}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={STROKE} />
                        Discard
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <AlertDialog open={!!discardTarget} onOpenChange={(v) => { if (!v) setDiscardTarget(null); }}>
        <AlertDialogContent data-testid="recovery-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-red-600" strokeWidth={STROKE} />
              Discard this recording permanently?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the on-device audio for{" "}
              <strong>
                {discardTarget?.candidateName || "this candidate"}
                {discardTarget?.roundNumber ? ` · Round ${discardTarget.roundNumber}` : ""}
              </strong>
              . It has not been saved to the server, so this cannot be undone. The interview will
              remain with no recording attached.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 rounded-md border border-emerald-800/20 bg-emerald-50/50 px-3 py-2 text-[12px] text-emerald-900">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={STROKE} />
            Prefer Recover if you can — it finishes saving the audio to this candidate&rsquo;s permanent record.
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recovery-discard-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDiscard}
              className="bg-red-600 hover:bg-red-700"
              data-testid="recovery-discard-confirm"
            >
              Discard permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
